import React, { FC, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { debounce } from 'lodash';
import { Field, useFormikContext } from 'formik';
import { styled } from 'linaria/react';
import { useBreakpoint } from '@mtsbank/ui-kit';
import { ServerOption } from '@entities/server';
import { GetPriceResp } from '@entities/voucher';
import { PaymentFormValues } from '@entities/payment';
import { useCyberStore, TspPagePayeeParam, TspPagePayee } from '@entities/tsp';
import { loginOrEmailErrorGtm, loginOrEmailGtm } from '@shared/lib/gtm';
import { COMPONENTS_BY_NAME, DESKTOP_TOOLTIP_BY_SERVICE_AND_NAME, IS_CLEARABLE_BY_NAME, MASK_BY_SERVICE_AND_NAME, MOBILE_LABEL_BY_SERVICE_AND_NAME } from '../config/fieldsParams';
import { getWrapperClass, getWrapperFieldClass } from '../lib/fieldClassnames';
import { getVisibleTspFields } from '../../../../entities/tsp/lib/getVisibleTspFields';
import { checkIsVoucher } from '../../../../entities/tsp/lib/checkIsVoucher';
import { useEmailTooltip } from '../lib/useEmailTooltip';
import { checkIsGameKey } from '../../../../entities/tsp/lib/checkIsGameKey';

// const DynamicPubgNarutoBanner = dynamic(() => import('./PubgNarutoBanner').then((imported) => imported.default), {
//   loading: () => <div />,
//   ssr: false,
// })

interface TspFieldsProps {
  priceData: GetPriceResp;
  activeService: TspPagePayee;
  servers: ServerOption[];
  getCommissions: (baseAmount: number) => void;
}
const TspFields: FC<TspFieldsProps> = ({
  priceData,
  activeService,
  servers,
  getCommissions
}) => {
  const router = useRouter();
  const {
    values,
    setFieldValue,
    validateForm,
    handleBlur
  } = useFormikContext<PaymentFormValues>();
  const {
    paymentMethod,
    activeDenomination
  } = useCyberStore();
  const {
    isMobile
  } = useBreakpoint();
  const {
    isTooltipVisible,
    showTooltip,
    hideTooltip
  } = useEmailTooltip();
  const debouncedGetCommission = useCallback(debounce((price: string) => {
    if (price) {
      getCommissions(Number(price));
    }
  }, 500, {
    leading: false,
    trailing: true
  }), [paymentMethod, activeDenomination]);
  useEffect(() => {
    setFieldValue('server', '');
  }, [router.asPath]);
  useEffect(() => {
    debouncedGetCommission(priceData?.price as string);
  }, [values?.server]);
  const getFieldName = (field: TspPagePayeeParam) => isVoucher || isGameKey ? 'email' : field.name;
  const getFieldLabel = (field: TspPagePayeeParam) => {
    if (isVoucher || isGameKey) {
      return 'E-mail';
    }
    const labelByServiceAndName = MOBILE_LABEL_BY_SERVICE_AND_NAME[activeService?.features?.tsp_url ?? '']?.[field.name];
    if (labelByServiceAndName) {
      return isMobile ? labelByServiceAndName : 'Где искать';
    }
    return field.title || activeService?.features?.textAbovePayButton;
  };
  const getFieldPlaceholder = (field: TspPagePayeeParam) => isVoucher || isGameKey ? 'Введите вашу личную почту' : field.description;
  const getToolTip = (field: TspPagePayeeParam) => {
    const tooltipByServiceAndName = DESKTOP_TOOLTIP_BY_SERVICE_AND_NAME[activeService?.features?.tsp_url ?? '']?.[field.name];
    if (tooltipByServiceAndName) {
      return isMobile ? '' : tooltipByServiceAndName;
    }
  };
  const getMask = (field: TspPagePayeeParam) => MASK_BY_SERVICE_AND_NAME[activeService?.features?.tsp_url ?? '']?.[field.name];
  const handleFieldChange = (field: TspPagePayeeParam) => {
    if (field.name === 'server') {
      return e => setFieldValue(getFieldName(field), e, true);
    }
    return e => setFieldValue(getFieldName(field), e.target.value.trimEnd());
  };
  const handleFieldFocus = (field: TspPagePayeeParam) => () => {
    if (field.name === 'id1') {
      if (values.email.length >= 3) {
        showTooltip();
      }
    }
  };
  const handleFieldBlur = (field: TspPagePayeeParam) => {
    if (field.name === 'id1' || field.name === 'zone' ||
    // новые
    field.name === 'region' || field.name === 'server' || field.name === 'uid' || field.name === 'uidPubg' || field.name === 'id' || field.name === 'zoneId' || field.name === 'playerId') {
      return async e => {
        hideTooltip();
        const validationErrors = await validateForm();
        const hasErrors = Object.keys(validationErrors).length > 0;
        if (isVoucher || isGameKey) {
          if (!validationErrors?.email) {
            loginOrEmailGtm(activeService?.providerName);
          } else {
            loginOrEmailErrorGtm(activeService?.providerName);
          }
        }
        if (!hasErrors && priceData?.price) {
          getCommissions(Number(priceData.price));
        }
        handleBlur(e);
      };
    }
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };
  const visibleFields = getVisibleTspFields(activeService);
  const isGameKey = checkIsGameKey(activeService);
  const isVoucher = checkIsVoucher(visibleFields, isGameKey);
  const fields = visibleFields.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)).map(field => ({
    options: servers,
    size: 'md',
    autoComplete: 'off',
    isClearable: IS_CLEARABLE_BY_NAME[field.name],
    name: getFieldName(field),
    label: getFieldLabel(field),
    placeholder: getFieldPlaceholder(field),
    // hint: getHint(),
    tooltip: getToolTip(field),
    mask: getMask(field),
    onChange: handleFieldChange(field),
    onFocus: handleFieldFocus(field),
    onBlur: handleFieldBlur(field),
    onKeyDown: handleKeyDown,
    className: 'field-input',
    component: COMPONENTS_BY_NAME[field.name]
  }));
  return <OuterWrapper>
      <Wrapper className={getWrapperClass(fields.length)}>
        {fields?.map((field, index) => <WrapperField key={field.name} className={getWrapperFieldClass(fields.length, index)}>
            <Field {...field} />
            {field.name === 'email' && isTooltipVisible && <TooltipWrapper>
                {/* <Tooltip open large color="blue" arrowPlacement="top">
                  Убедитесь, что вы верно ввели почту
                 </Tooltip> */}
              </TooltipWrapper>}
          </WrapperField>)}
      </Wrapper>
      {/* {partner === Partners.DEFAULT && isPubgMobile && <DynamicPubgNarutoBanner />} */}
    </OuterWrapper>;
};
export default TspFields;
const OuterWrapper = /*#__PURE__*/styled('div')({
  name: "OuterWrapper",
  class: "otkzte4",
  propsAsIs: false
});
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w1ciuegp",
  propsAsIs: false
});
const WrapperField = /*#__PURE__*/styled('div')({
  name: "WrapperField",
  class: "wnzdjey",
  propsAsIs: false
});
const TooltipWrapper = /*#__PURE__*/styled('div')({
  name: "TooltipWrapper",
  class: "t15j4m78",
  propsAsIs: false
});

require("./TspFields.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./TspFields.tsx");